import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    NgZone,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { EntityHistoryContentDialogComponent } from './entity-history-content-dialog.component';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { RouterModule } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime } from 'rxjs/operators';

import { DateTime } from 'luxon';

import { MatDialogSizes } from '../../../../enums/mat-dialog-sizes.enum';
import { LegFiJwtService } from '../../../../services/auth/legfi-jwt.service';
import { GrowlerService } from '../../../shared/growler/growler.service';
import { FlexSpacerDirective } from '../directives/flex-spacer.directive';
import { AppStatusChipComponent } from '../status-chip/status-chip.component';
import { SimpleBadgeComponent } from '../simple-badge/simple-badge.component';

import { EntityHistoryService } from './entity-history.service';
import {
    EntityHistoryEntry,
    EntityHistoryFilters,
    EntityHistoryResponse,
    EntityHistorySubjectType,
    subjectDisplayLabel,
} from './entity-history.types';

export interface EntityHistoryDialogInput
{
    subjectType: EntityHistorySubjectType;
    subjectId: number;
    subjectLabel?: string;
}

interface FilterForm
{
    eventTypes: FormControl<string[] | null>;
    from: FormControl<Date | null>;
    to: FormControl<Date | null>;
    actorId: FormControl<number | null>;
}

const DEFAULT_EVENTS: string[] = ['created', 'updated', 'deleted', 'restored'];

@UntilDestroy()
@Component({
    selector: 'app-entity-history-dialog',
    templateUrl: './entity-history-dialog.component.html',
    styleUrls: ['./entity-history-dialog.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatIconModule,
        MatProgressBarModule,
        MatTableModule,
        MatPaginatorModule,
        RouterModule,
        AppStatusChipComponent,
        SimpleBadgeComponent,
        FlexSpacerDirective,
    ],
})
export class EntityHistoryDialogComponent implements OnInit, OnDestroy
{
    @ViewChild('historyTableWrapper') historyTableWrapper?: ElementRef<HTMLElement>;

    readonly displayedColumns = ['occurredAt', 'actor', 'event', 'entity', 'details', 'source'];

    filterForm = new FormGroup<FilterForm>({
        eventTypes: new FormControl<string[]>([]),
        from: new FormControl<Date | null>(null),
        to: new FormControl<Date | null>(null),
        actorId: new FormControl<number | null>(null),
    });

    /**
     * Client-side quick-search across the loaded page. Matches against the
     * "What" (entity label) and "Changes" content of every row. Server-side
     * search would be a larger lift — the controller already loads every audit
     * into memory and paginates in PHP, so for the typical charge/invoice
     * subject this filter covers the full dataset on one page.
     */
    searchControl = new FormControl<string>('', { nonNullable: true });
    searchTerm = '';

    /** Rows currently displayed in the table (search-filtered subset). */
    visibleEntries: EntityHistoryEntry[] = [];

    availableEvents: string[] = [...DEFAULT_EVENTS];
    availableActors: { id: number; name: string }[] = [];

    entries: EntityHistoryEntry[] = [];
    loading = false;
    // Read-only dialog — no mutating submit/save actions. Declared so the
    // dialog-submit-prevention meta-lint rule recognizes the (intentional) absence.
    readonly isSaving = false;

    pageIndex = 0;
    perPage = 25;
    total = 0;

    expandedChanges = new Set<string>();
    /** Per-change-line expansion: keys are `${entry.id}:${field}`. */
    expandedValues = new Set<string>();
    expandedObjects = new Set<string>();

    /**
     * Track auto-expansions made by `applySearch()` so they can be rolled back
     * when the search term changes or clears — without disturbing rows the
     * user manually expanded. `toggleChanges` / `toggleObject` clear the
     * matching id from the auto-set so user clicks take ownership.
     */
    private _autoExpandedChanges = new Set<string>();
    private _autoExpandedObjects = new Set<string>();

    dialogTitle: string;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: EntityHistoryDialogInput,
        public _dialogRef: MatDialogRef<EntityHistoryDialogComponent>,
        private _service: EntityHistoryService,
        private _growler: GrowlerService,
        private _cdr: ChangeDetectorRef,
        private _dialog: MatDialog,
        private _zone: NgZone,
    )
    {
        const prefix = subjectDisplayLabel(data.subjectType);
        this.dialogTitle = data.subjectLabel
            ? `${ prefix } History — ${ data.subjectLabel }`
            : `${ prefix } History`;
    }

    ngOnInit(): void
    {
        this.filterForm.valueChanges
            .pipe(debounceTime(250), untilDestroyed(this))
            .subscribe(() => {
                this.pageIndex = 0;
                this.loadPage();
            });

        this.searchControl.valueChanges
            .pipe(debounceTime(150), untilDestroyed(this))
            .subscribe((term) => {
                this.searchTerm = (term ?? '').trim();
                this.applySearch();
                this._cdr.markForCheck();
                this._scheduleHighlightUpdate();
            });

        this.loadPage();
    }

    ngOnDestroy(): void
    {
        // Drop our named highlight + injected style tag on close so the
        // global registry stays clean.
        const supported = typeof CSS !== 'undefined' && (CSS as any).highlights;
        if (supported && this._highlightName) {
            (CSS as any).highlights.delete(this._highlightName);
        }
        if (this._highlightStyleEl?.parentNode) {
            this._highlightStyleEl.parentNode.removeChild(this._highlightStyleEl);
        }
    }

    // ----- Native CSS Custom Highlight API integration ------------------
    // Chrome 105+ / Safari 17.2+ / Firefox 140+. The browser highlights raw
    // text ranges at the rendering layer — zero DOM rewrites, zero re-render
    // cost on the bound text. Far cheaper than re-piping every change row
    // through innerHTML + DomSanitizer on every keystroke / expand toggle.
    //
    // Per-instance name + per-instance injected `<style>` so concurrent
    // dialogs (rare, but possible — admin lookup → drill-in) don't
    // overwrite each other's highlight registration on the global
    // `CSS.highlights` map.
    private static _highlightInstanceCounter = 0;
    private readonly _highlightName: string = `entity-history-search-${ ++EntityHistoryDialogComponent._highlightInstanceCounter }`;
    private _highlight?: any;
    private _highlightStyleEl?: HTMLStyleElement;

    private _ensureHighlightStyle(): void
    {
        if (this._highlightStyleEl || typeof document === 'undefined') {
            return;
        }
        // Color mirrors $app-warning-color (#FF7700) @ 35% alpha — kept in
        // sync with the matching commented-out static rule we used to ship
        // in the SCSS. Single source of truth for instances of this dialog.
        const style = document.createElement('style');
        style.setAttribute('data-entity-history-highlight', this._highlightName);
        style.textContent = `::highlight(${ this._highlightName }) { background-color: rgba(255, 119, 0, 0.35); border-radius: 2px; }`;
        document.head.appendChild(style);
        this._highlightStyleEl = style;
    }

    private _scheduleHighlightUpdate(): void
    {
        // Re-scan after the next CD pass so DOM reflects current visibleEntries
        // / expansion state. Run outside Angular so the rAF callback doesn't
        // schedule another change-detection tick.
        this._zone.runOutsideAngular(() => {
            requestAnimationFrame(() => this._updateHighlights());
        });
    }

    private _updateHighlights(): void
    {
        const w = window as any;
        if (typeof w.Highlight !== 'function'
            || typeof CSS === 'undefined'
            || !(CSS as any).highlights
        ) {
            return;  // Older browser — search still filters, just no highlight.
        }
        const container = this.historyTableWrapper?.nativeElement;
        if (!container) { return; }

        if (!this._highlight) {
            this._highlight = new w.Highlight();
            (CSS as any).highlights.set(this._highlightName, this._highlight);
            this._ensureHighlightStyle();
        }
        this._highlight.clear();

        const needle = this.searchTerm.toLowerCase();
        if (!needle) { return; }

        const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
        let node: Node | null;
        // eslint-disable-next-line no-cond-assign
        while ((node = walker.nextNode())) {
            const textNode = node as Text;
            const text = textNode.data;
            if (!text) { continue; }
            const lower = text.toLowerCase();
            let idx = 0;
            while ((idx = lower.indexOf(needle, idx)) !== -1) {
                const range = document.createRange();
                range.setStart(textNode, idx);
                range.setEnd(textNode, idx + needle.length);
                this._highlight.add(range);
                idx += needle.length;
            }
        }
    }

    /**
     * Recompute `visibleEntries` from the loaded `entries` and the current
     * `searchTerm`. Also auto-expands rows whose match lives in a truncated /
     * collapsed area, so the highlighted hit is visible without the user
     * clicking "show more" on every row.
     */
    private applySearch(): void
    {
        // Roll back any auto-expansions from the prior search. User-toggled
        // ids were removed from the auto-set by toggleChanges/toggleObject,
        // so they survive this reset.
        for (const id of this._autoExpandedChanges) {
            this.expandedChanges.delete(id);
        }
        for (const path of this._autoExpandedObjects) {
            this.expandedObjects.delete(path);
        }
        this._autoExpandedChanges.clear();
        this._autoExpandedObjects.clear();

        const needle = this.searchTerm.toLowerCase();
        if (!needle) {
            this.visibleEntries = [...this.entries];
            return;
        }
        this.visibleEntries = this.entries.filter((entry) => this.entryMatches(entry, needle));
        for (const entry of this.visibleEntries) {
            if (this.entryHasHiddenMatch(entry, needle)) {
                if (!this.expandedChanges.has(entry.id)) {
                    this.expandedChanges.add(entry.id);
                    this._autoExpandedChanges.add(entry.id);
                }
            }
            // Object-value matches won't be visible (or highlighted) until the
            // user drills into the nested object — auto-expand the matching
            // path so the hit is reachable in place.
            for (const change of entry.changes ?? []) {
                this._autoExpandObjectMatches(entry.id, change, needle);
            }
        }
    }

    private _autoExpandObjectMatches(
        entryId: string,
        change: EntityHistoryEntry['changes'][0],
        needle: string,
    ): void
    {
        const candidates: Array<{ suffix: string; value: unknown }> = [
            { suffix: '', value: change.oldValue },
            { suffix: '', value: change.newValue },
            { suffix: ':related', value: (change as { oldRelated?: unknown }).oldRelated },
            { suffix: ':related', value: (change as { newRelated?: unknown }).newRelated },
        ];
        for (const { suffix, value } of candidates) {
            if (!this.isObjectValue(value)) { continue; }
            if (!this.valueContains(change.field, value, needle)) { continue; }
            const path = `${ entryId }:${ change.field }${ suffix }`;
            if (!this.expandedObjects.has(path)) {
                this.expandedObjects.add(path);
                this._autoExpandedObjects.add(path);
            }
        }
    }

    private entryMatches(entry: EntityHistoryEntry, needle: string): boolean
    {
        if (this.textContains(entry.actor?.name, needle)) { return true; }
        if (this.textContains(this.eventLabelForEntry(entry), needle)) { return true; }
        if (this.textContains(this.entityLabel(entry), needle)) { return true; }
        if (this.textContains(entry.message, needle)) { return true; }
        for (const change of entry.changes ?? []) {
            if (this.changeMatches(change, needle)) {
                return true;
            }
        }
        return false;
    }

    private changeMatches(change: EntityHistoryEntry['changes'][0], needle: string): boolean
    {
        if (this.textContains(change.label, needle)) { return true; }
        if (this.textContains(change.field, needle)) { return true; }
        if (this.textContains(change.oldLabel, needle)) { return true; }
        if (this.textContains(change.newLabel, needle)) { return true; }
        if (this.valueContains(change.field, change.oldValue, needle)) { return true; }
        if (this.valueContains(change.field, change.newValue, needle)) { return true; }
        return false;
    }

    private entryHasHiddenMatch(entry: EntityHistoryEntry, needle: string): boolean
    {
        const changes = entry.changes ?? [];
        // Match lives in a collapsed change row (beyond the first 3).
        for (let i = 3; i < changes.length; i++) {
            if (this.changeMatches(changes[i], needle)) {
                return true;
            }
        }
        // Match lives in a truncated long value on one of the visible rows.
        for (const change of changes.slice(0, 3)) {
            if (this.isChangeLineTruncatable(change, entry.event) && this.changeMatches(change, needle)) {
                return true;
            }
        }
        return false;
    }

    private textContains(value: string | null | undefined, needle: string): boolean
    {
        if (!value) { return false; }
        return value.toLowerCase().includes(needle);
    }

    private valueContains(field: string | null, value: unknown, needle: string): boolean
    {
        if (value === null || value === undefined) { return false; }
        if (typeof value === 'object') {
            try {
                return JSON.stringify(value).toLowerCase().includes(needle);
            } catch {
                return false;
            }
        }
        const formatted = this.formatValue(field, value);
        return formatted.toLowerCase().includes(needle);
    }

    clearSearch(): void
    {
        this.searchControl.setValue('');
    }

    onPageChange(event: PageEvent): void
    {
        this.pageIndex = event.pageIndex;
        this.perPage = event.pageSize;
        this.loadPage();
    }

    trackById(index: number, entry: EntityHistoryEntry): string
    {
        return entry.id;
    }

    trackByEvent(index: number, evt: string): string
    {
        return evt;
    }

    trackByActor(index: number, actor: { id: number; name: string }): number
    {
        return actor.id;
    }

    trackByField(index: number, change: { field: string }): string
    {
        return change.field;
    }

    eventColor(event: string): 'success' | 'info' | 'warn' | 'error' | 'default'
    {
        switch (event) {
            case 'created': return 'success';
            case 'updated': return 'info';
            case 'deleted': return 'error';
            case 'restored': return 'warn';
            default: return 'default';
        }
    }

    eventLabel(event: string): string
    {
        if (!event) {
            return '';
        }
        return event.charAt(0).toUpperCase() + event.slice(1).replace(/[-_]/g, ' ');
    }

    /**
     * Context-aware label for the "Action" chip. Falls back to the generic
     * `eventLabel` for ordinary CRUD events; disambiguates Owen-IT's pivot
     * relation events (attach / detach / sync) using the change-row's
     * `field` + old/new values so users see e.g. "Shared with units" vs.
     * "Unshared from all units" instead of a bare "Sync".
     *
     * Owen-IT writes relation events with:
     *   - event=attach for auditSyncWithoutDetaching / auditAttach
     *   - event=detach for auditDetach
     *   - event=sync for auditSync (which can mean "replace all" or "clear")
     *
     * We pick the relation name from the change-row's `field` since
     * Owen-IT keys old/new_values by relation name.
     *
     * --- Current usage (verified 2026-05-29) ---
     * The only PHP callers of auditAttach/auditDetach/auditSync* in the
     * codebase are:
     *   - core/app/Http/Controllers/Documents/ShareDocumentController.php
     *     → $document->auditSyncWithoutDetaching('units', $unitIds)
     *   - core/app/Http/Controllers/Documents/UnshareDocumentController.php
     *     → $document->auditDetach('units', $unitIds)
     *     → $document->auditSync('units', [])
     *
     * So in practice today this branch only fires for OrganizationFile
     * share/unshare events, and the relation name is always `units`.
     *
     * The labeling logic below is intentionally GENERIC, not hard-coded to
     * documents/units: it reads the relation name straight from the change
     * row's `field`. If a future caller adds, say,
     *   $model->auditSyncWithoutDetaching('members', $ids)
     * it will render as "Shared with members" automatically with no
     * additional wiring needed. Verify with:
     *   grep -rnE 'auditAttach\\(|auditDetach\\(|auditSync\\(' core/app
     */
    eventLabelForEntry(entry: EntityHistoryEntry): string
    {
        const event = entry.event;
        if (!event) { return ''; }

        if (event === 'attach' || event === 'detach' || event === 'sync') {
            const change = (entry.changes ?? []).find(c => Array.isArray(c.newValue) || Array.isArray(c.oldValue));
            if (change) {
                const relation = change.label || change.field || 'relation';
                const newCount = Array.isArray(change.newValue) ? change.newValue.length : null;
                const oldCount = Array.isArray(change.oldValue) ? change.oldValue.length : null;

                if (event === 'attach') {
                    return `Shared with ${ relation.toLowerCase() }`;
                }
                if (event === 'detach') {
                    return `Unshared from ${ relation.toLowerCase() }`;
                }
                // sync
                if (newCount === 0 && (oldCount ?? 0) > 0) {
                    return `Unshared from all ${ relation.toLowerCase() }`;
                }
                if ((newCount ?? 0) > 0 && oldCount === 0) {
                    return `Shared with ${ relation.toLowerCase() }`;
                }
                return `${ relation } list replaced`;
            }
        }

        return this.eventLabel(event);
    }

    sourceBadgeLabel(source: 'audit' | 'activity'): string
    {
        return source === 'audit' ? 'Audit' : 'Activity';
    }

    /**
     * Human label for the underlying record this audit row is about. Renders
     * in the "What" column. Falls back to a dash when the backend didn't send
     * entity info (older response shape).
     */
    entityLabel(entry: EntityHistoryEntry): string
    {
        if (!entry.entity) {
            return '—';
        }
        const type = this.humanizeEntityType(entry.entity.type);
        return `${ type } #${ entry.entity.id }`;
    }

    /**
     * Friendly label for the `entity.type` discriminator. Backend emits a
     * single canonical camelCase key per the contract in
     * `entity-history.types.ts → EntityHistoryRecord.type` (source of truth
     * is `App\Support\Audit\AuditSubjectTypes::entityKey()` server-side).
     *
     * Anything not in the explicit map falls back to a camelCase splitter
     * (`paymentReturnedReason` → `Payment Returned Reason`) so the dialog
     * stays useful when new audited models surface before this map is
     * extended.
     */
    private humanizeEntityType(type: string): string
    {
        if (!type) {
            return 'Record';
        }
        const LABELS: Record<string, string> = {
            // Whitelisted subject types.
            address: 'Address',
            charge: 'Charge',
            document: 'Document',
            member: 'Member',
            organization: 'Organization',
            organizationProfile: 'Organization Profile',
            owner: 'Owner',
            payable: 'Payable',
            payableApproval: 'Payable Approval',
            payabliPayable: 'Payabli Payable',
            payment: 'Payment',
            paymentAttempt: 'Payment Attempt',
            paymentItem: 'Payment Item',
            recurringCharge: 'Recurring Charge',
            request: 'Request',
            transaction: 'Transaction',
            unit: 'Unit',
            user: 'User',
            violation: 'Violation',
            // Child auditables (not subjects themselves, surfaced alongside
            // their parent). Map values diverge from the camelCase key only
            // where the marketing label differs from the class name (e.g.
            // ChargeLateFee → "Late Fee" reads better in context than
            // "Charge Late Fee").
            chargeLateFee: 'Late Fee',
            paymentAttemptFee: 'Payment Fee',
            unitOccupancy: 'Unit Occupancy',
            membership: 'Membership',
            note: 'Note',
            auxiliaryEmail: 'Auxiliary Email',
            customFieldValue: 'Custom Field',
            customUnitFieldValue: 'Custom Field',
            violationStatementComment: 'Violation Comment',
        };
        if (LABELS[type]) {
            return LABELS[type];
        }
        // camelCase → Title Case fallback for unmapped keys.
        return type
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, (c) => c.toUpperCase())
            .trim();
    }

    /**
     * Field names that store monetary amounts in CENTS — we'll render with
     * the currency format ($X.XX). Extend this list as new fields surface.
     */
    /**
     * Field names whose values are integer cents and should render as currency
     * ($X.XX) in the audit dialog. Sourced from the relevant model `$casts`
     * + `$fillable` declarations. When adding a new audited model, scan its
     * money columns and add them here.
     *
     * Source models so far:
     *   - Charge: amount, balance, charge_amount, fee_amount, late_fee_amount,
     *     payments_sum, past_due_balance, opening balance (but boolean!)
     *   - PaymentAttempt: net, applied, gross, offloaded_fees, absorbed_fees,
     *     refunded_amount
     *   - PaymentItem: amount, total_amount
     *   - Transaction: amount
     *   - Unit: price, deposit_fees, pet_fee, utilities_amount,
     *     rent_late_fee_amount, rent_early_discount_amount
     *   - MembershipProfile: autopay_max
     */
    private readonly CURRENCY_FIELDS_IN_CENTS = new Set<string>([
        'amount',
        'balance',
        'subtotal',
        'discount',
        'tax',
        'total',
        'fee',
        'fee_amount',
        'charge_amount',
        'payments_sum',
        'past_due_balance',
        'late_fee_amount',
        'rent_late_fee_amount',
        'rent_early_discount_amount',
        'pet_fee',
        'deposit_fees',
        'price',
        'utilities_amount',
        'autopay_max',
        // PaymentAttempt money
        'net',
        'applied',
        'gross',
        'offloaded_fees',
        'absorbed_fees',
        'refunded_amount',
        // PaymentItem money
        'total_amount',
    ]);

    /**
     * Field names stored as integer 0/1 in the DB that we should render as
     * "true"/"false" instead of "0"/"1". Extend as more boolean fields surface.
     */
    /**
     * Field names whose 0/1 (or true/false) value should render as the word
     * "true" or "false" instead of a raw 0/1. Sourced from `$casts` =>
     * 'boolean' declarations on audited models. Add new entries when scanning
     * a model with boolean casts.
     *
     * Source models so far:
     *   - Charge: opening_balance (flag, despite name), paid, forgiven,
     *     email_invoice
     *   - PaymentAttempt: returned, disputed, pending, success, autopaid,
     *     absorb_fees, opening_balance
     *   - PaymentItem: (none currently)
     *   - User: super_user, locked
     *   - Membership: is_admin, is_ro_admin
     *   - MembershipProfile: autopay_enabled, has_profile_image,
     *     *_shared_on_directory (covered by 'directory' substring elsewhere)
     *   - Unit: has_images
     *   - Various: public, enabled, active, archived, deleted, completed,
     *     verified
     */
    private readonly BOOLEAN_FIELDS = new Set<string>([
        // Generic state flags
        'directory',
        'public',
        'enabled',
        'active',
        'archived',
        'deleted',
        'completed',
        'locked',
        'verified',
        // Charge
        'paid',
        'forgiven',
        'email_invoice',
        'opening_balance',
        // PaymentAttempt
        'returned',
        'disputed',
        'pending',
        'success',
        'autopaid',
        'absorb_fees',
        // User / Membership / MembershipProfile
        'autopay_enabled',
        'has_profile_image',
        'has_images',
        'is_admin',
        'is_ro_admin',
        'super_user',
    ]);

    formatValue(field: string | null, value: unknown): string
    {
        if (value === null || value === undefined || value === '') {
            return '—';
        }
        if (typeof value === 'object') {
            try {
                return JSON.stringify(value);
            } catch {
                return String(value);
            }
        }

        // Currency fields stored as integer cents.
        if (field && this.CURRENCY_FIELDS_IN_CENTS.has(field.toLowerCase()) && this.isNumeric(value)) {
            return this.formatCents(Number(value));
        }

        // Boolean fields stored as 0/1 in the DB → render as "false"/"true".
        if (field && this.BOOLEAN_FIELDS.has(field.toLowerCase())
            && (value === 0 || value === 1 || value === '0' || value === '1')
        ) {
            return (value === 1 || value === '1') ? 'true' : 'false';
        }

        if (typeof value === 'string') {
            // Strip "App\Models\Entities\..." namespaces in polymorphic *_type
            // values so they render readably (UnifiedBankAccount, not
            // App\Models\Entities\UnifiedBankAccount).
            if (value.includes('\\')) {
                const parts = value.split('\\');
                return parts[parts.length - 1];
            }

            const asDate = this.tryFormatDateString(value);
            if (asDate) {
                return asDate;
            }
        }
        return String(value);
    }

    private isNumeric(v: unknown): boolean
    {
        return typeof v === 'number' || (typeof v === 'string' && v !== '' && !isNaN(Number(v)));
    }

    private formatCents(cents: number): string
    {
        const dollars = cents / 100;
        const negative = dollars < 0;
        const abs = Math.abs(dollars);
        const formatted = abs.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        return negative ? `-${ formatted }` : formatted;
    }

    formatOccurredDate(iso: string | null | undefined): string
    {
        if (!iso) {
            return '';
        }
        const dt = this.parseInUserZone(iso);
        return dt.isValid ? dt.toFormat('MM/dd/yyyy') : '';
    }

    formatOccurredTime(iso: string | null | undefined): string
    {
        if (!iso) {
            return '';
        }
        const dt = this.parseInUserZone(iso);
        if (!dt.isValid) {
            return '';
        }
        // e.g. "4:52pm EDT" — lowercase only the AM/PM, keep timezone uppercase.
        return dt.toFormat('h:mm:ssa ZZZZ').replace(/(AM|PM)/, (m) => m.toLowerCase());
    }

    private parseInUserZone(value: string): DateTime
    {
        const timezone = LegFiJwtService.getTimezone();
        // Backend sends ISO 8601 UTC; also handle SQL datetime fallback.
        let dt = DateTime.fromISO(value, { zone: 'utc' });
        if (!dt.isValid) {
            dt = DateTime.fromSQL(value, { zone: 'utc' });
        }
        return dt.isValid ? dt.setZone(timezone) : dt;
    }

    private tryFormatDateString(value: string): string | null
    {
        // Matches ISO 8601 or SQL datetime ("YYYY-MM-DD" with optional "HH:mm:ss" or "T...").
        const looksLikeDate = /^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2}(:\d{2})?)?/.test(value);
        if (!looksLikeDate) {
            return null;
        }
        const dt = this.parseInUserZone(value);
        if (!dt.isValid) {
            return null;
        }
        const hasTime = value.includes(':');
        if (hasTime) {
            // 05/26/2026 4:00pm EDT
            return dt.toFormat('MM/dd/yyyy h:mm:ssa ZZZZ').replace(/(AM|PM)/, (m) => m.toLowerCase());
        }
        return dt.toFormat('MM/dd/yyyy');
    }

    toggleChanges(id: string): void
    {
        if (this.expandedChanges.has(id)) {
            this.expandedChanges.delete(id);
        } else {
            this.expandedChanges.add(id);
        }
        // User took ownership — drop from the auto-set so the next search
        // change won't roll back their manual choice.
        this._autoExpandedChanges.delete(id);
        this._cdr.markForCheck();
        this._scheduleHighlightUpdate();
    }

    isExpanded(id: string): boolean
    {
        return this.expandedChanges.has(id);
    }

    /**
     * Heuristic: a value is "truncatable" (worth showing a Show more affordance
     * for) when its displayed string is longer than the CSS max-width can fit
     * inline at the table's font size. ~30 chars matches the 220px clamp.
     */
    isTruncatable(field: string | null, value: unknown, label?: string | null): boolean
    {
        if (label) {
            // FK-resolved labels render as "label (id)" — count both.
            return (label.length + String(value ?? '').length + 3) > 30;
        }
        const formatted = this.formatValue(field, value);
        return formatted.length > 30;
    }

    /**
     * For an update event, the change line is truncatable if EITHER side
     * overflows. For created / deleted only the relevant side matters.
     */
    isChangeLineTruncatable(change: EntityHistoryEntry['changes'][0], event: string): boolean
    {
        if (!change) {
            return false;
        }
        if (event === 'created') {
            return this.isTruncatable(change.field, change.newValue, change.newLabel);
        }
        if (event === 'deleted') {
            return this.isTruncatable(change.field, change.oldValue, change.oldLabel);
        }
        return this.isTruncatable(change.field, change.oldValue, change.oldLabel)
            || this.isTruncatable(change.field, change.newValue, change.newLabel);
    }

    /**
     * Toggle truncation on a single change line (both old + new values are
     * expanded together — only one "show more" link per row).
     */
    toggleValue(entryId: string, field: string): void
    {
        const key = `${ entryId }:${ field }`;
        if (this.expandedValues.has(key)) {
            this.expandedValues.delete(key);
        } else {
            this.expandedValues.add(key);
        }
        this._cdr.markForCheck();
        this._scheduleHighlightUpdate();
    }

    /**
     * A value should render unwrapped when either:
     *   (a) its row is fully expanded ("Show less" mode), or
     *   (b) the user has clicked the "show more" link on this change line.
     */
    isValueExpanded(entryId: string, field: string): boolean
    {
        if (this.expandedChanges.has(entryId)) {
            return true;
        }
        return this.expandedValues.has(`${ entryId }:${ field }`);
    }

    /**
     * Detect values that should render as drill-down objects rather than
     * stringified content. Anything that's a non-null object or array
     * (i.e. nested model state) gets the expandable summary treatment.
     */
    /**
     * Heuristic: a string contains HTML markup we should render rather than
     * print as text. Matches when the value contains an HTML-tag-shaped token
     * (e.g. `<p>`, `<br />`, `<div class="x">`). Pure punctuation like
     * `<3` or `1 < 2` won't match.
     *
     * Used to switch the value-cell renderer from `{{ text }}` to
     * `[innerHTML]="text"` so the same content shown elsewhere in the app via
     * `[innerHTML]="value | nl2br"` renders consistently here too. Angular's
     * DomSanitizer strips dangerous tags by default.
     */
    containsHtml(value: unknown): boolean
    {
        if (typeof value !== 'string' || value === '') {
            return false;
        }
        return /<\/?[a-z][a-z0-9]*(\s[^<>]*)?\/?>/i.test(value);
    }

    /**
     * Plain-text preview of HTML content for the inline (collapsed) display.
     * Strips tags and decodes a handful of common entities so the collapsed
     * label reads naturally next to the "View full content" toggle.
     */
    /**
     * Whitelist of field names whose values are HTML payloads we should
     * render in a popup rather than as inline text. Kept as an explicit list
     * (instead of inferring from value content) so a stray `<3` in a regular
     * note doesn't accidentally trigger the HTML viewer. Extend conservatively
     * as new audited HTML fields surface.
     */
    private readonly HTML_FIELDS = new Set<string>([
        'message',  // violation_statement.message, violation_statement_comment.message
        'header',   // violation_statement.header
        'footer',   // violation_statement.footer
        'content',  // legacy_violation_history.content (email body snapshot)
        'body',     // generic email body column
    ]);

    /**
     * Decide whether a change row's value should be treated as renderable
     * HTML. Combines the field-name whitelist with a content sanity check —
     * we only flag a row as HTML when both the field is in the whitelist AND
     * the value actually contains tag-shaped markup. Prevents short / empty
     * notes on a `message` field from popping the viewer pointlessly.
     */
    isHtmlField(entry: EntityHistoryEntry, fieldName: string | null): boolean
    {
        if (!fieldName || !this.HTML_FIELDS.has(fieldName.toLowerCase())) {
            return false;
        }
        const change = (entry?.changes ?? []).find(c => c.field === fieldName);
        if (!change) {
            return false;
        }
        return this.containsHtml(change.newValue) || this.containsHtml(change.oldValue);
    }

    /**
     * Pop a read-only dialog showing the full HTML payload for a change row's
     * value. Title humanizes the field name so users see "Message", "Header",
     * etc. Angular sanitizes via DomSanitizer — no `bypassSecurityTrustHtml`.
     */
    openHtmlContent(html: unknown, field: string | null): void
    {
        if (typeof html !== 'string' || html === '') {
            return;
        }
        const title = this.humanizeFieldName(field);
        this._dialog.open(EntityHistoryContentDialogComponent, {
            width: MatDialogSizes.LG,
            maxWidth: '95vw',
            panelClass: 'entity-history-content-dialog',
            autoFocus: 'dialog',
            data: { title, html },
        });
    }

    private humanizeFieldName(field: string | null): string
    {
        if (!field) { return 'Content'; }
        return field
            .replace(/[_-]/g, ' ')
            .replace(/\b\w/g, c => c.toUpperCase());
    }

    htmlToPreview(value: unknown): string
    {
        if (typeof value !== 'string') {
            return '';
        }
        const stripped = value
            .replace(/<br\s*\/?>/gi, ' ')
            .replace(/<\/p>/gi, ' ')
            .replace(/<[^>]+>/g, '')
            .replace(/&nbsp;/g, ' ')
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/\s+/g, ' ')
            .trim();
        return stripped;
    }

    isObjectValue(value: unknown): boolean
    {
        return value !== null && typeof value === 'object';
    }

    /**
     * Compact one-line preview of an object/array value. Picks up to a couple
     * of "identifying" primitive fields and tells the user how many fields
     * exist in total. Used as the collapsed summary in the audit dialog.
     */
    objectSummary(value: unknown): string
    {
        if (Array.isArray(value)) {
            const n = value.length;
            return n === 0 ? '[no items]' : `[${ n } item${ n === 1 ? '' : 's' }]`;
        }
        if (!value || typeof value !== 'object') {
            return String(value);
        }
        const obj = value as Record<string, unknown>;
        const keys = Object.keys(obj);
        // Preferred preview fields, in order. First two non-null primitives win.
        const previewFields = [
            'id', 'name', 'title', 'label', 'subject', 'description',
            'email', 'unitNumber', 'givenNames', 'familyName',
            'fileName', 'fileType', 'street_address', 'line1',
        ];
        let parts: string[] = [];
        for (const k of previewFields) {
            if (!(k in obj)) { continue; }
            const v = obj[k];
            if (v === null || v === '' || typeof v === 'object') { continue; }
            parts = [...parts, `${ k }: ${ v }`];
            if (parts.length === 2) { break; }
        }
        const total = keys.length;
        const shown = parts.length;
        if (shown === 0) {
            return `{ ${ total } field${ total === 1 ? '' : 's' } }`;
        }
        const remaining = total - shown;
        return remaining > 0
            ? `{ ${ parts.join(', ') }, +${ remaining } more }`
            : `{ ${ parts.join(', ') } }`;
    }

    /**
     * Iterable [key, value] pairs for an object/array, for nested rendering.
     */
    objectEntries(value: unknown): Array<{ key: string; value: unknown }>
    {
        if (Array.isArray(value)) {
            return value.map((v, i) => ({ key: String(i), value: v }));
        }
        if (value && typeof value === 'object') {
            return Object.entries(value as Record<string, unknown>)
                .map(([k, v]) => ({ key: k, value: v }));
        }
        return [];
    }

    /**
     * Toggle expand state for a single nested object node. The `path` uniquely
     * identifies the node (e.g. "audit:123:organization.profile").
     */
    toggleObject(path: string): void
    {
        if (this.expandedObjects.has(path)) {
            this.expandedObjects.delete(path);
        } else {
            this.expandedObjects.add(path);
        }
        this._autoExpandedObjects.delete(path);
        this._cdr.markForCheck();
        this._scheduleHighlightUpdate();
    }

    isObjectExpanded(path: string): boolean
    {
        return this.expandedObjects.has(path);
    }

    trackByObjectEntry(_i: number, kv: { key: string; value: unknown }): string
    {
        return kv.key;
    }

    visibleChanges(entry: EntityHistoryEntry): EntityHistoryEntry['changes']
    {
        if (!entry.changes) {
            return [];
        }
        return this.isExpanded(entry.id) ? entry.changes : entry.changes.slice(0, 3);
    }

    hiddenChangeCount(entry: EntityHistoryEntry): number
    {
        if (!entry.changes || this.isExpanded(entry.id)) {
            return 0;
        }
        return Math.max(0, entry.changes.length - 3);
    }

    private loadPage(): void
    {
        const value = this.filterForm.value;
        this.loading = true;
        this._cdr.markForCheck();

        this._service.getPaginated(this.data.subjectType, this.data.subjectId, {
            page: this.pageIndex + 1,
            perPage: this.perPage,
            eventTypes: value.eventTypes ?? undefined,
            from: value.from ? this.toIsoDate(value.from) : undefined,
            to: value.to ? this.toIsoDate(value.to) : undefined,
            actorId: value.actorId ?? undefined,
        })
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (response: EntityHistoryResponse) => {
                    this.entries = response.data || [];
                    this.total = response.meta?.total ?? 0;
                    this.mergeFilters(response.filters);
                    this.applySearch();
                    this.loading = false;
                    this._cdr.markForCheck();
                    this._scheduleHighlightUpdate();
                },
                error: () => {
                    this.entries = [];
                    this.visibleEntries = [];
                    this.total = 0;
                    this.loading = false;
                    this._growler.oops('Unable to load history.');
                    this._cdr.markForCheck();
                },
            });
    }

    private mergeFilters(filters: EntityHistoryFilters | undefined): void
    {
        if (filters?.events?.length) {
            const merged = new Set<string>([...this.availableEvents, ...filters.events]);
            this.availableEvents = Array.from(merged);
        }
        if (filters?.actors?.length) {
            const byId = new Map<number, { id: number; name: string }>();
            [...this.availableActors, ...filters.actors].forEach((a) => byId.set(a.id, a));
            this.availableActors = Array.from(byId.values());
        }
    }

    private toIsoDate(date: Date): string
    {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${ y }-${ m }-${ d }`;
    }
}
